import React, { useCallback, useEffect, useState } from 'react';
import { Film, Play, Search, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtBytes, fmtLen, REC_TONE, sourceLabel } from '@/lib/recordingLibrary';
import { HlsPlayer } from '@/components/seller/videos/HlsPlayer';
import { ProductVideoLibrary } from '@/components/seller/videos/ProductVideoLibrary';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TONE = { green: 'bg-green-500/10 text-green-700', red: 'bg-red-500/10 text-red-600', amber: 'bg-amber-500/10 text-amber-700', gray: 'bg-black/5 dark:bg-white/10' };
const fmtDT = (s) => (s ? new Date(s).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }) : '-');
const fmtT = (s) => (s ? new Date(s).toLocaleTimeString('ko-KR', { hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-');
const field = 'px-2 py-1.5 rounded border border-black/15 dark:border-white/20 bg-transparent text-sm';

const StorageSummary = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/seller/recordings/summary').then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="recording-storage-summary">
      <div className="p-2 rounded-lg bg-black/5 dark:bg-white/5"><p className="text-gray-500">저장 완료 녹화</p><p className="font-bold text-sm" data-testid="summary-recordings">{s.recordings}건</p></div>
      <div className="p-2 rounded-lg bg-black/5 dark:bg-white/5"><p className="text-gray-500">방송 원본(HLS)</p><p className="font-bold text-sm" data-testid="summary-original-bytes">{fmtBytes(s.original_bytes)}{s.original_unknown ? ` + ${s.original_unknown}건 확인 중` : ''}</p></div>
      <div className="p-2 rounded-lg bg-black/5 dark:bg-white/5"><p className="text-gray-500">상품 소개영상 MP4</p><p className="font-bold text-sm" data-testid="summary-mp4-bytes">{fmtBytes(s.product_mp4_bytes)} · {s.product_mp4_objects}개</p></div>
      <div className="p-2 rounded-lg bg-black/5 dark:bg-white/5"><p className="text-gray-500">전체 방송 MP4</p><p className="font-bold text-sm" data-testid="summary-full-bytes">{s.full_mp4_bytes ? fmtBytes(s.full_mp4_bytes) : '없음'}</p></div>
    </div>
  );
};

const Filters = ({ f, setF }) => (
  <div className="flex flex-wrap gap-2 items-center" data-testid="recording-filters">
    <div className="relative flex-1 min-w-[160px]">
      <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
      <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="방송 제목 검색" className={`${field} w-full pl-7`} data-testid="recording-search-input" />
    </div>
    <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={field} aria-label="시작일" data-testid="recording-date-from" />
    <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={field} aria-label="종료일" data-testid="recording-date-to" />
    <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={field} data-testid="recording-status-filter">
      <option value="">전체 상태</option><option value="completed">저장 완료</option><option value="verifying">저장 확인 중</option>
      <option value="recording">녹화 중</option><option value="failed">녹화 실패</option><option value="interrupted">녹화 중단</option><option value="missing">원본 없음</option>
    </select>
    <select value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} className={field} data-testid="recording-sort">
      <option value="new">최신순</option><option value="old">오래된순</option>
    </select>
  </div>
);

const RecordingCard = ({ r, onOpen }) => (
  <button type="button" onClick={() => onOpen(r.session_id)} className="w-full text-left p-3 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/50 transition-colors space-y-1" data-testid={`recording-card-${r.session_id}`}>
    <div className="flex items-start gap-2">
      <Film size={16} className="mt-0.5 text-gray-400 shrink-0" />
      <p className="font-bold text-sm flex-1 line-clamp-2 break-all" data-testid="recording-card-title">{r.title || '(제목 없음)'}</p>
      <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${TONE[REC_TONE[r.status]]}`} data-testid="recording-card-status">{r.status_label}</span>
    </div>
    <p className="text-xs text-gray-500">{fmtDT(r.recording_started_at || r.broadcast_started_at)} · 방송 {fmtT(r.broadcast_started_at)}~{fmtT(r.broadcast_ended_at)}</p>
    <p className="text-xs text-gray-500" data-testid="recording-card-meta">
      {r.source === 'clean' ? '클린 녹화' : '방송 화면 녹화'} · 길이 {fmtLen(r.duration_sec)} · 원본 {fmtBytes(r.original_bytes)} · 상품 {r.linked_products}개 · 소개영상 {r.ready_videos}/{r.intro_segments}
    </p>
    <p className="text-[11px] text-gray-400" data-testid="recording-card-download">{r.playable ? '전체 재생 가능' : '재생 불가'} · {r.download.label}</p>
  </button>
);

const DownloadBox = ({ d, sessionId, onChanged }) => {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (d.state !== 'processing') return undefined;
    const t = setInterval(onChanged, 5000);
    return () => clearInterval(t);
  }, [d.state, onChanged]);
  const prepare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/seller/recordings/${sessionId}/full-mp4`);
      toast.success(data.queued ? '전체 MP4 준비를 시작했습니다 · 완료되면 자동으로 표시됩니다.' : data.already_ready ? '이미 준비된 MP4가 있습니다.' : '이미 준비 중입니다.');
      onChanged();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det === 'full_mp4_disabled' ? '전체 MP4 준비 기능이 아직 꺼져 있습니다.' : '전체 MP4 준비 요청 실패');
    } finally { setBusy(false); }
  };
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/seller/recordings/${sessionId}/download-url`);
      window.location.href = data.url;
    } catch { toast.error('다운로드 주소를 받지 못했습니다.'); } finally { setBusy(false); }
  };
  return (
    <div className="p-2 rounded-lg bg-black/5 dark:bg-white/5 text-xs space-y-1" data-testid="recording-download-box">
      <p className="font-bold">전체 녹화 다운로드 (MP4 파일 · 위 전체 재생과 별도)</p>
      <p data-testid="recording-download-state">{d.label}{d.size ? ` · ${fmtBytes(d.size)}` : ''}</p>
      {d.state === 'failed' && d.error && <p className="text-red-600 break-all" data-testid="recording-download-error">{d.error}</p>}
      {d.state === 'processing' && <p className="text-gray-500 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" />변환 중 · 5초마다 상태를 확인합니다.</p>}
      {d.state === 'ready' && <button type="button" disabled={busy} onClick={go} className="px-3 py-1.5 rounded-full bg-[#FF3B30] text-white font-bold inline-flex items-center gap-1" data-testid="recording-download-btn"><Download size={12} />MP4 다운로드</button>}
      {['needs_prepare', 'failed'].includes(d.state) && (d.can_prepare
        ? <button type="button" disabled={busy} onClick={prepare} className="px-3 py-1.5 rounded-full border border-[#FF3B30] text-[#FF3B30] font-bold disabled:opacity-40" data-testid="recording-prepare-btn">{busy ? '요청 중…' : '전체 녹화 MP4 준비'}</button>
        : <p className="text-gray-500">원본은 HLS(여러 조각 파일)로 저장되어 있어 바로 내려받을 수 없습니다. 전체 MP4 준비 기능은 현재 꺼져 있습니다.</p>)}
    </div>
  );
};

const REPLAY_WHY = { recording_interrupted: '녹화가 중단되어 공개할 수 없습니다.', recording_failed: '녹화에 실패한 방송입니다.', recording_in_progress: '녹화 저장이 끝나지 않았습니다.', recording_missing: '저장된 원본이 없습니다.', broadcast_not_public: '비공개·숨김 방송은 공개할 수 없습니다.', broadcast_not_ended: '종료된 방송만 공개할 수 있습니다.' };

const ReplayToggle = ({ d, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const why = d.replay_block_reason;
  const set = async (on) => {
    setBusy(true);
    try {
      await api.put(`/seller/recordings/${d.session_id}/replay`, { public: on });
      toast.success(on ? '다시보기를 공개했습니다 · 판매자 프로필에 표시됩니다.' : '다시보기 공개를 중지했습니다 · 소비자 재생이 즉시 차단됩니다.');
      onChanged();
    } catch (e) { toast.error(REPLAY_WHY[e?.response?.data?.detail] || '변경 실패'); } finally { setBusy(false); }
  };
  return (
    <div className="p-2 rounded-lg bg-black/5 dark:bg-white/5 text-xs space-y-1" data-testid="replay-toggle-box">
      <p className="font-bold">소비자 다시보기 공개 <span data-testid="replay-state" className={d.replay_public && !why ? 'text-green-700' : 'text-gray-500'}>{d.replay_public ? (why ? 'ON · 공개 조건 미충족으로 노출 안 됨' : 'ON · 공개 중') : 'OFF · 비공개'}</span></p>
      {why && <p className="text-amber-700" data-testid="replay-block-reason">{REPLAY_WHY[why] || why}</p>}
      <p className="text-gray-500">전체 녹화에는 방송 중 모든 장면·소개 상품이 포함됩니다. 공개 전 내용을 확인해 주세요. 상품 소개영상 공개 설정과는 별개입니다.</p>
      {d.replay_public
        ? <button type="button" disabled={busy} onClick={() => set(false)} className="px-3 py-1.5 rounded-full border font-bold" data-testid="replay-off-btn">다시보기 공개 중지</button>
        : <button type="button" disabled={busy || !!why} onClick={() => set(true)} className="px-3 py-1.5 rounded-full bg-[#FF3B30] text-white font-bold disabled:opacity-30" data-testid="replay-on-btn">다시보기 공개하기</button>}
    </div>
  );
};

const RecordingDetail = ({ sessionId, onClose, onShowVideos }) => {
  const [d, setD] = useState(null);
  const [src, setSrc] = useState(null);
  const refresh = useCallback(() => {
    api.get(`/seller/recordings/${sessionId}`).then((r) => setD(r.data)).catch(() => {});
  }, [sessionId]);
  useEffect(() => {
    setD(null); setSrc(null);
    api.get(`/seller/recordings/${sessionId}`).then((r) => setD(r.data)).catch(() => { toast.error('녹화를 찾을 수 없습니다.'); onClose(); });
  }, [sessionId, onClose]);
  const play = async () => {
    try { const { data } = await api.get(`/seller/recordings/${sessionId}/play-url`); setSrc(`${BACKEND}${data.url}`); } catch { toast.error('재생할 수 없는 녹화입니다.'); }
  };
  if (!d) return <p className="text-xs text-gray-500" data-testid="recording-detail-loading"><Loader2 size={12} className="inline animate-spin" /> 불러오는 중…</p>;
  return (
    <div className="space-y-3" data-testid="recording-detail">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onClose} className="text-xs px-2 py-1 rounded border" data-testid="recording-detail-back">← 목록</button>
        <p className="font-black flex-1 truncate" data-testid="recording-detail-title">{d.title}</p>
        <span className={`text-[11px] px-1.5 py-0.5 rounded ${TONE[REC_TONE[d.status]]}`} data-testid="recording-detail-status">{d.status_label}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-gray-600 dark:text-gray-300" data-testid="recording-detail-meta">
        <span>방송 {fmtDT(d.broadcast_started_at)} ~ {fmtT(d.broadcast_ended_at)}</span><span>녹화 길이 {fmtLen(d.duration_sec)}</span>
        <span>원본 크기 {fmtBytes(d.original_bytes)} · {d.hls_segment_count || 0}개 조각</span><span>상품영상 합계 {fmtBytes(d.product_mp4_bytes)}</span>
        <span className="sm:col-span-2">{sourceLabel(d.source)}</span>
        {d.last_error && <span className="sm:col-span-2 text-red-600">오류: {d.last_error}</span>}
      </div>
      {d.playable && (src
        ? <HlsPlayer src={src} />
        : <button type="button" onClick={play} className="px-4 py-2 rounded-full bg-[#FF3B30] text-white text-sm font-bold inline-flex items-center gap-1" data-testid="recording-play-btn"><Play size={14} />전체 녹화 재생</button>)}
      <DownloadBox d={d.download} sessionId={sessionId} onChanged={refresh} />
      <ReplayToggle d={d} onChanged={refresh} />
      <div className="space-y-1" data-testid="recording-detail-videos">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold">이 방송의 상품 소개영상 ({d.ready_videos}/{d.intro_segments} READY)</p>
          <button type="button" onClick={() => onShowVideos(d.schedule_id)} className="text-xs text-[#FF3B30] font-bold" data-testid="recording-detail-to-videos">상품영상 관리 →</button>
        </div>
        {d.videos.map((v) => (
          <p key={v.segment_id} className="text-xs text-gray-600 dark:text-gray-300" data-testid={`recording-detail-video-${v.segment_id}`}>
            {fmtT(v.started_at)} · {v.product_name || v.product_id || '미연결'} · {fmtLen(v.playable?.duration_sec)}{v.playable ? ` · v${v.playable.version}` : ' · 미생성'}{v.versions?.length > 1 ? ` (보관 ${v.versions.length}개 버전)` : ''}{v.hidden ? ' · 소비자 숨김' : ''}
          </p>
        ))}
      </div>
    </div>
  );
};

const RecordingList = ({ onOpen }) => {
  const [f, setF] = useState({ q: '', date_from: '', date_to: '', status: '', sort: 'new' });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const load = useCallback((p) => {
    const params = { page: p, page_size: 20, sort: f.sort };
    ['q', 'date_from', 'date_to', 'status'].forEach((k) => { if (f[k]) params[k] = f[k]; });
    api.get('/seller/recordings', { params }).then(({ data: d }) => setData((prev) => (p > 1 && prev ? { ...d, items: [...prev.items, ...d.items] } : d)))
      .catch(() => setData({ items: [], total: 0 }));
  }, [f]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(1); }, 300); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-3">
      <StorageSummary />
      <Filters f={f} setF={setF} />
      {!data && <p className="text-xs text-gray-500">불러오는 중…</p>}
      {data && !data.items.length && <p className="text-sm text-gray-500 py-6 text-center" data-testid="recording-list-empty">조건에 맞는 녹화 기록이 없습니다. 녹화를 켠 방송이 저장되면 이곳에 표시됩니다.</p>}
      <div className="grid gap-2 md:grid-cols-2" data-testid="recording-list">{(data?.items || []).map((r) => <RecordingCard key={r.session_id} r={r} onOpen={onOpen} />)}</div>
      {data?.has_more && <button type="button" onClick={() => { const n = page + 1; setPage(n); load(n); }} className="w-full py-2 rounded border text-xs" data-testid="recording-load-more">더 보기 ({data.items.length}/{data.total})</button>}
    </div>
  );
};

export default function RecordingLibrary({ initialSchedule }) {
  const [tab, setTab] = useState('recordings');
  const [open, setOpen] = useState(null);
  const [videoSchedule, setVideoSchedule] = useState(null);
  useEffect(() => {
    if (!initialSchedule) return;
    api.get('/seller/recordings', { params: { schedule_id: initialSchedule, page_size: 1 } })
      .then(({ data }) => { if (data.items?.[0]) setOpen(data.items[0].session_id); }).catch(() => {});
  }, [initialSchedule]);
  const close = useCallback(() => setOpen(null), []);
  const tabBtn = (k, label) => (
    <button type="button" onClick={() => { setTab(k); setOpen(null); }} className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === k ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500'}`} data-testid={`video-library-tab-${k}`}>{label}</button>
  );
  return (
    <div className="space-y-4" data-testid="video-library">
      <div>
        <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>녹화·영상 관리</h2>
        <p className="text-xs text-gray-500">방송 전체 녹화와 상품 소개영상을 한곳에서 확인합니다. 상품관리의 라이브 소개영상과 같은 데이터를 사용합니다.</p>
      </div>
      <div className="flex border-b border-black/10 dark:border-white/10">{tabBtn('recordings', '전체 방송 녹화')}{tabBtn('videos', '상품 소개영상')}</div>
      {tab === 'recordings' && (open
        ? <RecordingDetail sessionId={open} onClose={close} onShowVideos={(sch) => { setVideoSchedule(sch); setOpen(null); setTab('videos'); }} />
        : <RecordingList onOpen={setOpen} />)}
      {tab === 'videos' && <ProductVideoLibrary scheduleId={videoSchedule} onClearSchedule={() => setVideoSchedule(null)} />}
    </div>
  );
}
